import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import InterventionTimer from "@/components/InterventionTimer";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";
import ChantierGpsPicker from "@/components/ChantierGpsPicker";
import ShareLinkPanel from "@/components/ShareLinkPanel";
import DebriefRecorder from "@/components/DebriefRecorder";
import DebriefSummaryCard from "@/components/DebriefSummaryCard";
import ReportHistoryList from "@/components/ReportHistoryList";
import InstalledEquipmentCard from "@/components/InstalledEquipmentCard";
import { CheckCircle, FilePdf, EnvelopeSimple } from "@phosphor-icons/react";
import SousChantiersSection from "../sections/SousChantiersSection";
import AssigneesSection from "../sections/AssigneesSection";
import QuoteAndTemplateSection from "../sections/QuoteAndTemplateSection";
import FinaliserDevisSection from "../sections/FinaliserDevisSection";
import ReportMetaSection from "../sections/ReportMetaSection";
import ReportTemplatePicker from "../sections/ReportTemplatePicker";
import ChecklistSection from "../sections/ChecklistSection";
import ChantierRatingsCard from "@/components/ChantierRatingsCard";
import DevisHistorySection from "@/components/DevisHistorySection";

export default function OverviewTab({
  chantier,
  client,
  interventions,
  tasks,
  doneTasks,
  totalTasks,
  newTask,
  setNewTask,
  addTask,
  toggleTask,
  deleteTask,
  onChanged,
  onOpenEmail,
  onOpenQuoteAccepted,
}) {
  const { isAdmin } = useAuth();
  // Default: recap PDF enabled — set to false on a chantier only if the
  // admin toggles it OFF from the section below.
  const recapEnabled = chantier?.recap_pdf_enabled !== false;
  const [savingRecap, setSavingRecap] = useState(false);

  const openPdf = (variant = "field") => {
    const token = ""; // httpOnly cookie auth
    const rt = chantier?.report_template_id ? `&report_template_id=${chantier.report_template_id}` : "";
    window.open(`${API}/chantiers/${chantier.id}/report-pdf?variant=${variant}${rt}&auth=${encodeURIComponent(token)}`, "_blank");
  };

  const openRecapPdf = () => {
    window.open(`${API}/chantiers/${chantier.id}/recap-pdf`, "_blank");
  };

  const toggleRecap = async () => {
    setSavingRecap(true);
    try {
      await api.patch(`/chantiers/${chantier.id}`, { recap_pdf_enabled: !recapEnabled });
      toast.success(!recapEnabled ? "Récap PDF activé" : "Récap PDF désactivé");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de basculer le récap");
    } finally {
      setSavingRecap(false);
    }
  };

  const cur = interventions.find((i) => i.status !== "done") || interventions[0];

  return (
    <>
      <SousChantiersSection chantier={chantier} onChanged={onChanged} isAdmin={isAdmin} />

      <AssigneesSection chantier={chantier} onChanged={onChanged} />

      <QuoteAndTemplateSection chantier={chantier} onChanged={onChanged} />

      <FinaliserDevisSection chantier={chantier} onChanged={onChanged} />

      <DevisHistorySection chantierId={chantier?.id} />

      <ChantierRatingsCard chantierId={chantier?.id} interventions={interventions} isAdmin={isAdmin} />

      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={() => openPdf("field")}
          className="rounded-2xl bg-white/[0.03] border border-white/10 hover:border-amber-500/40 p-3 flex items-center gap-2 active:scale-[0.98] transition-transform"
          data-testid="pdf-field-btn"
        >
          <div className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
            <FilePdf size={18} weight="duotone" className="text-amber-400" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="font-display font-semibold text-[11px]">Terrain</div>
            <div className="text-[9px] text-white/50 truncate">Détails techniques</div>
          </div>
        </button>
        <button
          onClick={() => openPdf("client")}
          className="rounded-2xl bg-white/[0.03] border border-white/10 hover:border-[#005CFF]/40 p-3 flex items-center gap-2 active:scale-[0.98] transition-transform"
          data-testid="pdf-client-btn"
        >
          <div className="w-9 h-9 rounded-xl bg-[#005CFF]/15 flex items-center justify-center shrink-0">
            <FilePdf size={18} weight="duotone" className="text-[#005CFF]" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="font-display font-semibold text-[11px]">Client</div>
            <div className="text-[9px] text-white/50 truncate">Version épurée</div>
          </div>
        </button>
        <button
          onClick={onOpenEmail}
          className="rounded-2xl bg-white/[0.03] border border-white/10 hover:border-emerald-500/40 p-3 flex items-center gap-2 active:scale-[0.98] transition-transform"
          data-testid="email-report-btn"
        >
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
            <EnvelopeSimple size={18} weight="duotone" className="text-emerald-400" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="font-display font-semibold text-[11px]">Envoyer</div>
            <div className="text-[9px] text-white/50 truncate">{client?.email || "Email"}</div>
          </div>
        </button>
      </div>

      {/* Récap Chantier PDF — end-of-chantier one-pager ready to attach to Axonaut */}
      {isAdmin && (
        <div
          className="rounded-2xl bg-gradient-to-br from-violet-500/10 to-violet-500/[0.02] border border-violet-500/25 p-3"
          data-testid="recap-pdf-section"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center shrink-0">
              <FilePdf size={20} weight="duotone" className="text-violet-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-sm">Récap chantier PDF</div>
              <div className="text-[10px] text-white/50">
                Matériel installé · heures · équipe · debriefs · prêt pour Axonaut
              </div>
            </div>
            <button
              type="button"
              onClick={toggleRecap}
              disabled={savingRecap}
              role="switch"
              aria-checked={recapEnabled}
              aria-label={recapEnabled ? "Désactiver le Récap PDF" : "Activer le Récap PDF"}
              className={`relative h-6 w-11 rounded-full transition-colors shrink-0 disabled:opacity-50 ${
                recapEnabled ? "bg-violet-500" : "bg-white/10"
              }`}
              data-testid="recap-pdf-toggle"
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  recapEnabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
          {recapEnabled && (
            <button
              type="button"
              onClick={openRecapPdf}
              className="mt-3 w-full h-9 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-xs font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="recap-pdf-open-btn"
            >
              <FilePdf size={14} weight="fill" />
              Générer le Récap PDF
            </button>
          )}
        </div>
      )}

      {isAdmin && chantier?.to_quote && !chantier?.quote_accepted && (
        <button
          onClick={onOpenQuoteAccepted}
          className="w-full rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/5 border border-emerald-500/30 hover:border-emerald-500/60 p-3 flex items-center gap-3 active:scale-[0.99] transition-transform"
          data-testid="mark-quote-accepted-btn"
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
            <CheckCircle size={20} weight="duotone" className="text-emerald-300" />
          </div>
          <div className="flex-1 text-left">
            <div className="font-display font-semibold text-sm">Devis accepté par le client</div>
            <div className="text-[10px] text-white/50">Notifie BKpay et tous les webhooks abonnés</div>
          </div>
        </button>
      )}
      {chantier?.quote_accepted && (
        <div className="w-full rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-3 flex items-center gap-2 text-emerald-300 text-xs" data-testid="quote-accepted-badge">
          <CheckCircle size={16} weight="fill" /> Devis accepté{chantier.quote_accepted_by_name ? ` par ${chantier.quote_accepted_by_name}` : ""} · envoyé aux logiciels de facturation
        </div>
      )}

      {cur && (
        <div className="space-y-2" data-testid="field-ops-section">
          <div className="text-[11px] uppercase tracking-widest text-white/50 flex items-center gap-2">
            Terrain
            <span className="text-white/30">·</span>
            <span className="text-white/40 normal-case tracking-normal">{cur.title || "Intervention"}</span>
          </div>
          <InterventionTimer interventionId={cur.id} onUpdate={onChanged} />
          <VoiceNoteRecorder interventionId={cur.id} notes={cur.voice_notes || []} onSaved={onChanged} />
        </div>
      )}

      {isAdmin && <ChantierGpsPicker chantier={chantier} onSaved={onChanged} />}

      <DebriefRecorder chantierId={chantier.id} />

      <DebriefSummaryCard chantier={chantier} onChanged={onChanged} />

      <InstalledEquipmentCard chantier={chantier} />

      {isAdmin && <ShareLinkPanel chantier={chantier} onChanged={onChanged} />}

      <ReportMetaSection chantier={chantier} onChanged={onChanged} />

      <ReportTemplatePicker chantier={chantier} onChanged={onChanged} />

      <ReportHistoryList chantierId={chantier.id} />

      <ChecklistSection
        tasks={tasks}
        doneTasks={doneTasks}
        totalTasks={totalTasks}
        newTask={newTask}
        setNewTask={setNewTask}
        addTask={addTask}
        toggleTask={toggleTask}
        deleteTask={deleteTask}
        onTaskUpdate={onChanged}
      />
    </>
  );
}
